class AdawatShoppableVideo extends HTMLElement {
  connectedCallback() {
    if (this._adawatBooted) return;
    const boot = () => this.boot();
    if (window.AdawatLazyInit?.whenVisible) {
      window.AdawatLazyInit.whenVisible(this, boot);
    } else {
      boot();
    }
  }

  boot() {
    if (this._adawatBooted) return;
    this._adawatBooted = true;

    this.viewport = this.querySelector('[data-sv-viewport]');
    this.track = this.querySelector('[data-sv-track]');
    this.prevBtn = this.querySelector('[data-sv-prev]');
    this.nextBtn = this.querySelector('[data-sv-next]');
    this.progressBar = this.querySelector('[data-sv-progress]');
    this.section = this.closest('.adawat-shoppable-video');
    this.lightbox = this.section?.querySelector('[data-sv-lightbox]');
    this.lightboxVideo = this.lightbox?.querySelector('[data-sv-lightbox-video]');
    this.lightboxProduct = this.lightbox?.querySelector('[data-sv-lightbox-product]');
    this.lightboxList = this.lightbox?.querySelector('[data-sv-lightbox-list]');
    this.canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    this.isCoarse = window.matchMedia('(pointer: coarse)').matches;
    this.isMobile = window.matchMedia('(max-width: 767px)').matches || this.isCoarse;

    if (!this.viewport || !this.track) return;

    this.originalSlides = Array.from(this.track.querySelectorAll('[data-sv-slide]'));
    if (this.originalSlides.length === 0) return;

    // Infinite continuous scroll — never hard-stop / jump to first
    this.looping = this.originalSlides.length > 1;
    this.setWidth = 0;
    this.dragging = false;
    this.dragMoved = false;
    this.axisLocked = null;
    this.startX = 0;
    this.startY = 0;
    this.startScroll = 0;
    this.pointerId = null;
    this.suppressLoop = false;
    this.scrollRaf = 0;
    this.smoothScrollActive = false;

    this.stripHeavyVideos();

    if (this.looping) this.buildLoop();

    this.slides = Array.from(this.track.querySelectorAll('[data-sv-slide]'));
    this.bindSlideInteractions(this.slides);

    this.prevBtn?.addEventListener('click', () => this.scrollByDir(-1));
    this.nextBtn?.addEventListener('click', () => this.scrollByDir(1));
    if (this.prevBtn) this.prevBtn.disabled = false;
    if (this.nextBtn) this.nextBtn.disabled = false;

    this.viewport.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.viewport.addEventListener('scrollend', () => this.handleScroll(true), { passive: true });

    // Mouse drag; touch uses native overflow (still infinite via loop).
    if (!this.isMobile) {
      this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.viewport.addEventListener('pointermove', (event) => this.onPointerMove(event), {
        passive: false,
      });
      this.viewport.addEventListener('pointerup', (event) => this.onPointerUp(event));
      this.viewport.addEventListener('pointercancel', (event) => this.onPointerUp(event));
      this.viewport.addEventListener('lostpointercapture', (event) => this.onPointerUp(event));
    }

    this.viewport.addEventListener(
      'click',
      (event) => {
        if (this.dragMoved) {
          event.preventDefault();
          event.stopPropagation();
          this.dragMoved = false;
        }
      },
      true
    );

    this.lightbox?.querySelectorAll('[data-sv-lightbox-close]').forEach((el) => {
      el.addEventListener('click', () => this.closeLightbox());
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeLightbox();
    });

    this.onResize = () => {
      const wasMobile = this.isMobile;
      this.isMobile = window.matchMedia('(max-width: 767px)').matches || this.isCoarse;
      if (wasMobile !== this.isMobile) return;
      this.measure();
      this.centerLoop();
      this.updateProgress();
    };
    window.addEventListener('resize', this.onResize);

    requestAnimationFrame(() => {
      this.measure();
      this.centerLoop();
      this.updateProgress();
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
  }

  /** Keep posters only in the carousel; load video in lightbox. */
  stripHeavyVideos() {
    this.track.querySelectorAll('video').forEach((video) => {
      const slide = video.closest('[data-sv-slide]');
      const src =
        slide?.dataset.svVideoSrc ||
        video.currentSrc ||
        video.src ||
        video.getAttribute('data-src') ||
        video.querySelector('source')?.src ||
        '';

      if (slide && src && !slide.dataset.svVideoSrc) {
        slide.dataset.svVideoSrc = src;
      }

      video.removeAttribute('src');
      video.querySelectorAll('source').forEach((source) => source.remove());
      video.removeAttribute('data-src');
      video.preload = 'none';
      try {
        video.load();
      } catch (_) {
        /* ignore */
      }
    });
  }

  buildLoop() {
    // Two clone sets on each side = large buffer so swipe never hits a wall
    const makeClones = (label) => {
      const frag = document.createDocumentFragment();
      this.originalSlides.forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.setAttribute('data-sv-clone', label);
        clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        frag.appendChild(clone);
      });
      return frag;
    };

    this.track.insertBefore(makeClones('before-2'), this.track.firstChild);
    this.track.insertBefore(makeClones('before'), this.track.firstChild);
    this.track.appendChild(makeClones('after'));
    this.track.appendChild(makeClones('after-2'));
  }

  bindSlideInteractions(slides) {
    slides.forEach((slide) => {
      const media = slide.querySelector('[data-sv-media], .adawat-sv-card__media');
      const openBtn = slide.querySelector('[data-sv-open]');

      openBtn?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.openLightbox(slide);
      });

      media?.addEventListener('click', (event) => {
        if (event.target.closest('a, button')) return;
        event.preventDefault();
        this.openLightbox(slide);
      });
    });
  }

  getGap() {
    return parseFloat(getComputedStyle(this.track).gap) || 16;
  }

  measure() {
    if (!this.looping) {
      this.setWidth = 0;
      return;
    }

    // 5 equal sets: before-2, before, original, after, after-2
    const total = this.viewport.scrollWidth;
    if (total > 0) {
      this.setWidth = total / 5;
      return;
    }

    const originals = this.track.querySelectorAll('[data-sv-slide]:not([data-sv-clone])');
    if (!originals.length) {
      this.setWidth = 0;
      return;
    }

    const first = originals[0];
    const last = originals[originals.length - 1];
    this.setWidth = last.offsetLeft + last.offsetWidth - first.offsetLeft + this.getGap();
  }

  /** Land in the middle (original) set without animation */
  centerLoop() {
    if (!this.looping || !this.setWidth) return;
    this.jumpTo(this.setWidth * 2);
  }

  jumpTo(left) {
    this.suppressLoop = true;
    const prevBehavior = this.viewport.style.scrollBehavior;
    this.viewport.style.scrollBehavior = 'auto';
    this.viewport.scrollLeft = left;
    // Force reflow so browsers apply the jump before re-enabling loop
    void this.viewport.scrollLeft;
    requestAnimationFrame(() => {
      this.viewport.style.scrollBehavior = prevBehavior || '';
      this.suppressLoop = false;
      this.updateProgress();
    });
  }

  onScroll() {
    if (this.scrollRaf) return;
    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = 0;
      this.handleScroll();
    });
  }

  handleScroll(force = false) {
    if ((this.suppressLoop && !force) || !this.looping || !this.setWidth) {
      this.updateProgress();
      return;
    }

    let left = this.viewport.scrollLeft;
    const set = this.setWidth;
    // Keep scroll position inside the middle 3 sets (indices 1–3 of 0–4)
    // so user can always keep scrolling either direction without a hard end.
    const min = set * 1.05;
    const max = set * 3.95;

    if (left < min) {
      while (left < min) left += set;
      this.jumpTo(left);
      return;
    }

    if (left > max) {
      while (left > max) left -= set;
      this.jumpTo(left);
      return;
    }

    this.updateProgress();
  }

  updateProgress() {
    if (!this.progressBar) return;

    // Arrows stay enabled forever in loop mode
    if (this.prevBtn) this.prevBtn.disabled = false;
    if (this.nextBtn) this.nextBtn.disabled = false;

    if (!this.looping || !this.setWidth) {
      const max = Math.max(1, this.viewport.scrollWidth - this.viewport.clientWidth);
      const pct = (Math.abs(this.viewport.scrollLeft) / max) * 100;
      this.progressBar.style.width = `${Math.max(8, Math.min(100, pct || 8))}%`;
      return;
    }

    let pos = this.viewport.scrollLeft % this.setWidth;
    if (pos < 0) pos += this.setWidth;
    const pct = (pos / this.setWidth) * 100;
    this.progressBar.style.width = `${Math.max(8, Math.min(100, pct || 8))}%`;
  }

  scrollByDir(direction) {
    const slide = this.track.querySelector('[data-sv-slide]');
    if (!slide) return;
    const step = slide.getBoundingClientRect().width + this.getGap();
    const isRtl = document.documentElement.dir === 'rtl';
    const delta = (isRtl ? -direction : direction) * step;

    // Instant step when looping — smooth scroll fights the wrap teleport
    if (this.looping) {
      this.viewport.style.scrollBehavior = 'auto';
      this.viewport.scrollLeft += delta;
      this.handleScroll(true);
      return;
    }

    this.viewport.scrollBy({ left: delta, behavior: 'smooth' });
  }

  onPointerDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (event.target.closest('a, button')) return;

    this.dragging = true;
    this.dragMoved = false;
    this.axisLocked = 'x';
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.startScroll = this.viewport.scrollLeft;
    this.pointerId = event.pointerId;
    this.viewport.classList.add('is-grabbing');

    try {
      this.viewport.setPointerCapture?.(event.pointerId);
    } catch (_) {
      /* ignore */
    }
  }

  onPointerMove(event) {
    if (!this.dragging || event.pointerId !== this.pointerId) return;

    const dx = event.clientX - this.startX;

    if (!this.dragMoved && Math.abs(dx) > 6) {
      this.dragMoved = true;
      this.viewport.classList.add('is-dragging');
      this.pauseAllHover();
    }

    if (!this.dragMoved) return;

    this.viewport.scrollLeft = this.startScroll - dx;
    this.handleScroll();
    event.preventDefault();
  }

  onPointerUp(event) {
    if (!this.dragging || (this.pointerId != null && event.pointerId !== this.pointerId)) return;

    this.dragging = false;
    this.axisLocked = null;
    this.pointerId = null;
    this.viewport.classList.remove('is-dragging', 'is-grabbing');

    try {
      this.viewport.releasePointerCapture?.(event.pointerId);
    } catch (_) {
      /* ignore */
    }

    this.handleScroll(true);

    if (!this.dragMoved) return;
    window.setTimeout(() => {
      this.dragMoved = false;
    }, 0);
  }

  getSlideVideo(slide) {
    return slide?.querySelector('.adawat-sv-card__media video') || null;
  }

  ensureVideoSrc(slide) {
    const video = this.getSlideVideo(slide);
    const src = slide?.dataset.svVideoSrc || '';
    if (!video || !src) return video;
    if (!video.src && !video.currentSrc) {
      video.src = src;
    }
    return video;
  }

  hoverPlay(slide) {
    const video = this.ensureVideoSrc(slide);
    if (!video) return;
    slide.classList.add('is-hover-playing');
    video.muted = true;
    video.playsInline = true;
    video.play().catch(() => {});
  }

  hoverPause(slide) {
    const video = this.getSlideVideo(slide);
    slide?.classList.remove('is-hover-playing');
    if (!video) return;
    video.pause();
    try {
      video.currentTime = 0;
    } catch (_) {
      /* ignore */
    }
  }

  pauseAllHover() {
    this.slides.forEach((slide) => this.hoverPause(slide));
  }

  openLightbox(slide) {
    if (!this.lightbox || !this.lightboxVideo || !slide) return;
    this.pauseAllHover();

    const src = slide.dataset.svVideoSrc || '';
    if (!src) return;

    this.lightboxVideo.pause();
    this.lightboxVideo.src = src;
    this.lightboxVideo.muted = false;
    this.lightboxVideo.controls = true;
    this.lightboxVideo.setAttribute('playsinline', '');
    this.fillLightboxProduct(slide);
    this.lightbox.classList.add('is-open');
    this.lightbox.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('adawat-sv-lightbox-open');
    this.lightboxVideo.play().catch(() => {});
  }

  fillLightboxProduct(slide) {
    if (!this.lightboxProduct || !this.lightboxList) return;

    const template = slide?.querySelector('[data-sv-products-template]');
    const html = template?.innerHTML?.trim() || '';

    if (!html) {
      this.lightboxList.innerHTML = '';
      this.lightboxProduct.hidden = true;
      this.lightboxProduct.classList.remove('has-two');
      return;
    }

    this.lightboxList.innerHTML = html;
    const count = this.lightboxList.querySelectorAll('.adawat-sv-lightbox__product-row').length;
    this.lightboxProduct.classList.toggle('has-two', count >= 2);
    this.lightboxProduct.hidden = false;
    window.AdawatReapplySale?.();
  }

  closeLightbox() {
    if (!this.lightbox || !this.lightboxVideo) return;
    this.lightboxVideo.pause();
    this.lightboxVideo.removeAttribute('src');
    this.lightboxVideo.load();
    if (this.lightboxList) this.lightboxList.innerHTML = '';
    if (this.lightboxProduct) this.lightboxProduct.hidden = true;
    this.lightbox.classList.remove('is-open');
    this.lightbox.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('adawat-sv-lightbox-open');
  }
}

if (!customElements.get('adawat-shoppable-video')) {
  customElements.define('adawat-shoppable-video', AdawatShoppableVideo);
}
